import {
  FieldEditReasonEnum,
  FormChangesDto,
  FormChangingActionType,
  FormSkipReason,
} from 'src/services/api/api-client';
import Style from './VersionContainer.module.scss';
import { ActionLine } from './HistoryLeftBar.component';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { getActorName, hasSameEditReason, resolveChangingTime } from './historyHelper';
import { useTranslation } from 'react-i18next';
import { ReactComponent as UpdateIcon } from '../../../../assets/img/initialsurvey/update_16.svg';
import { ReactComponent as IncorrectEntryIcon } from '../../../../assets/img/initialsurvey/bold_cross_16.svg';
import { ReactComponent as CommentIcon } from '../../../../assets/img/formBuilder/statusIcons/chat_filled_16.svg';
import { DiffValueComponent } from './diffValueComponent/diffValueHistory.component';
import { useCallback, useMemo } from 'react';
import { SelectedVersion } from './HistoryTab';
import { useContextSelector } from 'use-context-selector';
import { FormFillContext } from 'src/components/formEditor/uiEditor/provider/formFill.context';
import { getEditReasonDataKeysCorrectOrder } from 'src/components/formEditor/uiEditor/uiEditor-helper';

export type VersionContainerProps = {
  currentVersion: FormChangesDto;
  hasEcrf?: boolean;
  previousVersion?: FormChangesDto;
  selectedCard: SelectedVersion;
  formResultId: number;
};

const editReasonIcons = new Map<FieldEditReasonEnum, JSX.Element>([
  [FieldEditReasonEnum.Update, <UpdateIcon className={Style.icon} key={FieldEditReasonEnum.Update} />],
  [FieldEditReasonEnum.WrongEntry, <IncorrectEntryIcon className={Style.icon} key={FieldEditReasonEnum.WrongEntry} />],
]);

export const VersionContainer = ({ currentVersion, hasEcrf }: VersionContainerProps) => {
  const { t } = useTranslation();
  const formLayout = useContextSelector(FormFillContext, (x) => x.nodes);
  const { versionNumber, formChangingActionType, fieldEditReasons, skipReasonDetails } = currentVersion;

  const sortedEditReasonsKeys = getEditReasonDataKeysCorrectOrder(formLayout!, fieldEditReasons);
  const majorVersion = hasEcrf ? t('Forms.HistoryOfChanges.Version', { version: versionNumber }) : '';
  const actorFullName = getActorName(currentVersion.formChangingActionType, currentVersion);
  const actionTime = localFormat(resolveChangingTime(currentVersion) ?? currentVersion.updatedAt, DateFormats.dateTime);
  const hasSameChangesReasons = hasSameEditReason(fieldEditReasons, 'editReason');
  const hasSameEditReasonText = hasSameEditReason(fieldEditReasons, 'editReasonText');

  const getFormEditReason = useCallback(
    (editReason: FieldEditReasonEnum): string | undefined => {
      switch (editReason) {
        case FieldEditReasonEnum.WrongEntry:
          return t('Forms.Overview.EditFieldReasonIcon.WrongEntry');
        case FieldEditReasonEnum.Update:
          return t('Forms.Overview.EditFieldReasonIcon.Update');
      }
    },
    [t],
  );

  const skippedBody = useMemo(() => {
    const translatedSkipReason =
      currentVersion?.skipReason === FormSkipReason.NotApplicable
        ? t('Forms.FormSkipReason.NotApplicable')
        : currentVersion?.skipReason === FormSkipReason.Missed
        ? t('Forms.FormSkipReason.Missed')
        : undefined;

    return (
      <>
        <div className={Style.columnContainer}>
          <span className={Style.title}>{t('Forms.HistoryOfChanges.SelectedVersion.SkippingReason')}</span>
          {translatedSkipReason}
        </div>
        <div className={Style.columnContainer}>
          <span className={Style.title}>{t('Forms.HistoryOfChanges.SelectedVersion.SkippingDetails')}</span>
          <span>{skipReasonDetails}</span>
        </div>
      </>
    );
  }, [currentVersion?.skipReason, skipReasonDetails, t]);

  const recalledBody = useMemo(() => {
    return (
      <div className={Style.columnContainer}>
        <span className={Style.title}>{t('Forms.HistoryOfChanges.SelectedVersion.RevokingReason')}</span>
        <span>{currentVersion?.approvalRecallReason}</span>
      </div>
    );
  }, [currentVersion, t]);

  const editedBody = useMemo(() => {
    {
      return (
        <>
          {sortedEditReasonsKeys.map((key, index) => {
            return (
              <div className={Style.lineOfChange} key={key}>
                <div className={Style.actionInfo}>
                  {(!hasSameChangesReasons || (hasSameChangesReasons && index === 0)) && (
                    <>
                      {editReasonIcons.get(fieldEditReasons[key].editReason)}
                      {getFormEditReason(fieldEditReasons[key].editReason)}
                    </>
                  )}
                </div>
                <div className={Style.changesInfo}>
                  <DiffValueComponent dataKey={key} />
                  {((fieldEditReasons[key].editReasonText && !hasSameEditReasonText) ||
                    (fieldEditReasons[key].editReasonText &&
                      hasSameEditReasonText &&
                      index === sortedEditReasonsKeys.length - 1)) && (
                    <div className={hasSameEditReasonText ? Style.sameCommentSection : ''}>
                      <CommentIcon className={Style.icon} /> <span>{fieldEditReasons[key].editReasonText}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      );
    }
  }, [fieldEditReasons, getFormEditReason, hasSameChangesReasons, hasSameEditReasonText, sortedEditReasonsKeys]);

  const body = useMemo(() => {
    switch (formChangingActionType) {
      case FormChangingActionType.Recall:
        return recalledBody;
      case FormChangingActionType.Skipped:
        return skippedBody;
      case FormChangingActionType.ChangeFields:
        return editedBody;
      default:
        return <></>;
    }
  }, [formChangingActionType, recalledBody, skippedBody, editedBody]);

  return (
    <div className={Style.versionContainer}>
      <div className={Style.topBar}>
        <ActionLine actorName={actorFullName!} formChangingType={formChangingActionType} version={majorVersion} />
        <span className={Style.time}>{actionTime}</span>
      </div>
      <div className={Style.editingInfo}>{body}</div>
    </div>
  );
};
